import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  Room as RoomIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Reservation } from '../../../../shared/services/reservation/reservationService';

interface ReservationDetailProps {
  reservation: Reservation;
  onEdit?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
}

export function ReservationDetail({ 
  reservation, 
  onEdit, 
  onDelete, 
  onClose 
}: ReservationDetailProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/reservations');
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTimeOnly = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDuration = () => {
    const start = new Date(reservation.startDate);
    const end = new Date(reservation.endDate);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h${diffMinutes > 0 ? ` ${diffMinutes}min` : ''}`;
    }
    return `${diffMinutes}min`;
  };

  const getStatus = () => {
    const now = new Date();
    const start = new Date(reservation.startDate);
    const end = new Date(reservation.endDate);
    
    if (now < start) return { label: 'Agendada', color: 'primary' };
    if (now >= start && now <= end) return { label: 'Em andamento', color: 'success' };
    return { label: 'Finalizada', color: 'default' };
  };

  // Verificar se a reserva pode ser editada (ainda não aconteceu)
  const canEdit = () => {
    const now = new Date();
    const start = new Date(reservation.startDate);
    return now < start; // Só pode editar se ainda não começou
  };

  // Verificar se a reserva pode ser deletada (ainda não aconteceu ou não terminou)
  const canDelete = () => {
    const now = new Date();
    const end = new Date(reservation.endDate);
    return now < end; // Pode deletar se ainda não terminou
  };

  const status = getStatus();
  const isEditable = canEdit();
  const isDeletable = canDelete();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header com navegação e ações */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={handleBack} 
              sx={{ 
                backgroundColor: theme.palette.action.hover,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                {reservation.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={status.label}
                  color={status.color as any}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" color="text.secondary">
                  • ID: {reservation.id.slice(0, 8)}...
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {onEdit && (
              <Tooltip 
                title={isEditable ? "Editar Reserva" : "Não é possível editar uma reserva que já aconteceu ou está em andamento"}
              >
                <span>
                  <IconButton 
                    onClick={isEditable ? onEdit : undefined}
                    disabled={!isEditable}
                    sx={{ 
                      backgroundColor: isEditable ? theme.palette.primary.main : theme.palette.action.disabled,
                      color: isEditable ? 'white' : theme.palette.text.disabled,
                      '&:hover': {
                        backgroundColor: isEditable ? theme.palette.primary.dark : theme.palette.action.disabled
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabled,
                        color: theme.palette.text.disabled
                      }
                    }}
                  >
                    {isEditable ? <EditIcon /> : <LockIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip 
                title={isDeletable ? "Excluir Reserva" : "Não é possível excluir uma reserva que já terminou"}
              >
                <span>
                  <IconButton 
                    onClick={isDeletable ? onDelete : undefined}
                    disabled={!isDeletable}
                    sx={{ 
                      backgroundColor: isDeletable ? theme.palette.error.main : theme.palette.action.disabled,
                      color: isDeletable ? 'white' : theme.palette.text.disabled,
                      '&:hover': {
                        backgroundColor: isDeletable ? theme.palette.error.dark : theme.palette.action.disabled
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabled,
                        color: theme.palette.text.disabled
                      }
                    }}
                  >
                    {isDeletable ? <DeleteIcon /> : <LockIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Aviso se não pode editar/deletar */}
        {(!isEditable || !isDeletable) && (
          <Box sx={{ mt: 2, mb:2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              backgroundColor: theme.palette.warning.light,
              p: 1.5,
              borderRadius: 1,
              color: theme.palette.warning.dark
            }}>
              <LockIcon fontSize="small" />
              {!isEditable && !isDeletable 
                ? "Esta reserva já aconteceu e não pode mais ser modificada."
                : !isEditable 
                ? "Esta reserva já aconteceu ou está em andamento e não pode ser editada."
                : "Esta reserva já terminou e não pode ser excluída."
              }
            </Typography>
          </Box>
        )}


      <Grid container spacing={3}>
        {/* Informações principais da reserva */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, minHeight: 310 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                  <EventIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  Detalhes da Reserva
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.background.default, borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CalendarIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Data
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDateOnly(reservation.startDate)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.background.default, borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScheduleIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        Duração
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {getDuration()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Horário de Início
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                      {formatTimeOnly(reservation.startDate)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Horário de Fim
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                      {formatTimeOnly(reservation.endDate)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Informações do local e responsável */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Local */}
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`,maxHeight: 200 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ backgroundColor: theme.palette.secondary.main }}>
                    <RoomIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight={600}>
                    Local
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={500} color="text.primary">
                  {reservation.roomName || 'Não informado'}
                </Typography>
              </CardContent>
            </Card>

            {/* Responsável */}
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight={600}>
                    Responsável
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={500} color="text.primary" gutterBottom>
                  {reservation.userName || 'Não informado'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {reservation.tenantName || 'Empresa não informada'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Informações do sistema */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Informações do Sistema
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Criado em
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(reservation.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Última atualização
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(reservation.updatedAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ID da Reserva
                  </Typography>
                  <Typography 
                    variant="body1" 
                    fontWeight={500} 
                    sx={{ 
                      fontFamily: 'monospace',
                      backgroundColor: theme.palette.action.hover,
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}
                  >
                    {reservation.id}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Paper>

    </Box>
  );
}